/**
 * Vincent #164/#165 (Eric 2026-05-24) — Parcours d'intégration + d'apprentissage
 *
 * Une seule page paramétrée par le prop `pathwayType` :
 *   - "onboarding" → Parcours d'intégration (groupes canoniques : nouveau/forme/avance/admin)
 *   - "learning"   → Parcours d'apprentissage (groupes custom : directeurs, conseillers, ...)
 *
 * Permet aux formateurs et fondateurs de :
 *   - lister les parcours existants
 *   - créer / éditer / supprimer un parcours
 *   - ajouter et réordonner des vidéos (drag = + / - dans la liste)
 *   - publier / dépublier (les apprenants ne voient que les parcours publiés)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useLang } from "../contexts/LanguageContext";
import { useAuth, useRoleLabel } from "../contexts/AuthContext";
import { getCanonicalRoles, CANONICAL_ROLE_IDS, roleLabelsFor } from "../lib/canonicalRoles";
import PathwayLearnerModal from "../components/pathways/PathwayLearnerModal";
import { usePathwayQuizEligibility, pathwayQuizButtonClasses } from "../lib/usePathwayQuizEligibility";
import { foldAccents } from "../lib/textFold"; // Tâche 1170
import {
  Plus, Trash, Eye, ArrowUp, ArrowDown, FloppyDisk, X, Path, Users, ArrowsClockwise, CaretDown, CaretRight, Cube, MagnifyingGlass,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// Vincent 2026-06-01 — Source de vérité UNIQUE : les rôles canoniques de
// "Gestion des rôles". Plus de listes hardcodées séparées pour onboarding
// vs learning ni de groupes custom. Les ID viennent de lib/canonicalRoles.js,
// les libellés peuvent être personnalisés par org (roleLabelsFor(user, lang)).
// Mapping pour migrer les anciennes target_groups (nouveau/forme/avance/...)
// vers les IDs canoniques sans appel backend.
const LEGACY_GROUP_MAP = {
  nouveau: "employee_new",
  forme: "employee_trained",
  avance: "employee_advanced",
  admin: "admin",
  conseiller: "employee",
};

function normalizeGroup(g) {
  return LEGACY_GROUP_MAP[g] || g;
}

function L(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function PathwaysPage({ pathwayType }) {
  const { lang } = useLang();
  const { user, refreshUser } = useAuth();
  // Vincent B571 (Eric 2026-07-27) — Utiliser les labels FR personnalisés
  // (Fondateur, Adjoint...) au lieu des IDs bruts (founder, employee...).
  const roleLabel = useRoleLabel();
  const navigate = useNavigate();
  const [pathways, setPathways] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vincent #190 (Eric 2026-05-27 round 5) — Tri des parcours.
  const [sortBy, setSortBy] = useState("name_asc"); // Vincent XLS iter103 — défaut = alphabétique A→Z
  const [editing, setEditing] = useState(null); // pathway object or "new"
  // Vincent #262 (Eric 2026-02-06) — Modal d'aperçu : clic sur la tuile
  // (hors boutons Éditer/Publier/Supprimer) ouvre la liste complète des
  // vidéos cliquables + bouton "Quiz du parcours".
  const [previewing, setPreviewing] = useState(null);
  // Vincent #269 (2026-06-11) — Visualisation apprenant (Mode de démonstration)
  const [viewingAsLearner, setViewingAsLearner] = useState(null);
  const [groupSuggestions, setGroupSuggestions] = useState([]);

  // Vincent B623 (Eric 2026-07-30 SPEC STRICK — PROD) — Collapse/expand par
  // parcours (vue admin/designer). Persistance : preferences.pathway_last_
  // opened_admin_{type}. Défaut : dernier consulté ou 1er alphabétique.
  // Vincent B631 (Eric 2026-07-31 SPEC STRICK PROD) — Passage à une VRAIE
  // mémorisation multi-ouverts. La préférence stocke un array d'IDs.
  const PREF_KEY = `pathway_open_ids_admin_${pathwayType}`;
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [expandInitialized, setExpandInitialized] = useState(false);

  const isOnboarding = pathwayType === "onboarding";
  const isWeekly = pathwayType === "weekly";
  // Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3 titres possibles selon
  // le type de parcours. Le reste de la page fonctionne à l'identique.
  const pageTitle = isOnboarding
    ? L(lang, "Parcours d'intégration", "Onboarding Pathways")
    : isWeekly
      ? L(lang, "Formations hebdomadaires", "Weekly Trainings")
      : L(lang, "Parcours d'apprentissage", "Learning Pathways");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, vRes] = await Promise.all([
        axios.get(`${API}/api/pathways?pathway_type=${pathwayType}`, { withCredentials: true }),
        // Vincent #178 (Eric 2026-05-26) — Charger TOUTES les vidéos de l'org
        // (pas seulement status=ready qui n'existe pas chez Knoria — c'est
        // pipeline_status="complete"). On retire le filtre status pour que
        // l'utilisateur voie TOUS les fichiers visibles dans l'explorateur,
        // y compris ceux en cours d'analyse. Limit max (500) pour éviter
        // qu'une org riche ne se retrouve avec une fenêtre vide.
        // Vincent B1102 (Eric 2026-09-07) — `kind=all` : sans ce paramètre,
        // /api/videos EXCLUT les documents texte (`is_text_doc=true`). Les
        // documents texte assignés à un parcours étaient donc comptés sur la
        // carte mais INVISIBLES dans l'éditeur (et supprimés du parcours au
        // prochain enregistrement).
        axios.get(`${API}/api/videos?limit=500&kind=all`, { withCredentials: true }),
      ]);
      setPathways(pRes.data.pathways || []);
      const videos = vRes.data.items || vRes.data.videos || vRes.data || [];
      setAllVideos(Array.isArray(videos) ? videos : []);
      if (!isOnboarding) {
        try {
          const gRes = await axios.get(`${API}/api/pathways/groups/list`, { withCredentials: true });
          setGroupSuggestions(gRes.data.groups || []);
        } catch (_) { /* ignore */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec du chargement", "Load failed"));
    } finally {
      setLoading(false);
    }
  }, [pathwayType, isOnboarding, lang]);

  useEffect(() => { load(); }, [load]);

  // Vincent B623 (Eric 2026-07-30 SPEC STRICK PROD) — Détermine l'état
  // initial d'expansion.
  // Vincent B631 (Eric 2026-07-31 SPEC STRICK PROD) — Support multi-ouverts :
  // la préférence est désormais un ARRAY d'IDs ouverts. Fallback sur
  // l'ancienne clé single-ID pour rétrocompatibilité.
  useEffect(() => {
    if (expandInitialized || loading || pathways.length === 0) return;
    const alphaSorted = [...pathways].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base", numeric: true })
    );
    const validIds = new Set(alphaSorted.map((p) => p.id));
    const raw = user?.preferences?.[PREF_KEY];
    const legacyLastId = user?.preferences?.[`pathway_last_opened_admin_${pathwayType}`];
    let next;
    if (Array.isArray(raw)) {
      next = new Set(raw.filter((id) => validIds.has(id)));
    } else if (typeof legacyLastId === "string" && validIds.has(legacyLastId)) {
      next = new Set([legacyLastId]);
    } else {
      next = new Set([alphaSorted[0].id]);
    }
    setExpandedIds(next);
    setExpandInitialized(true);
  }, [pathways, loading, expandInitialized, user?.preferences, PREF_KEY, pathwayType]);

  const toggleExpand = async (pathwayId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const wasOpen = expandedIds.has(pathwayId);
    const nextSet = new Set(expandedIds);
    if (wasOpen) nextSet.delete(pathwayId); else nextSet.add(pathwayId);
    setExpandedIds(nextSet);
    const nextArray = [...nextSet].sort();
    try {
      await axios.patch(
        `${API}/api/auth/me/preferences`,
        { [PREF_KEY]: nextArray },
        { withCredentials: true }
      );
      if (typeof refreshUser === "function") await refreshUser();
    } catch { /* silencieux */ }
  };

  const startNew = () => setEditing({
    id: null,
    pathway_type: pathwayType,
    name: "",
    description: "",
    // Vincent 2026-06-01 — Pré-sélection par défaut alignée sur les IDs canoniques.
    target_groups: isOnboarding ? ["employee_new"] : [],
    video_ids: [],
    is_published: false,
  });

  const removePathway = async (p) => {
    const ok = await confirmDialog(L(lang, `Supprimer "${p.name}" ?`, `Delete "${p.name}"?`));
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/pathways/${p.id}`, { withCredentials: true });
      toast.success(L(lang, "Parcours supprimé", "Pathway deleted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    }
  };

  // Vincent XLSX 2026-07-09 #4 (Eric 2026-07-09 SPEC STRICK) — Bouton manuel
  // "Réinitialiser les résultats" pour l'admin/concepteur : quand une vidéo est
  // ajoutée à un parcours, permet de remettre à 0 le quiz pour les utilisateurs
  // qui avaient déjà complété toutes les autres vidéos du parcours à 100%.
  const resetPathwayResults = async (p) => {
    const msg = L(
      lang,
      `Réinitialiser les résultats du parcours "${p.name}" ?\n\nCela remettra le score de quiz à 0 pour les utilisateurs qui avaient déjà TERMINÉ toutes les vidéos du parcours (utile après l'ajout d'une nouvelle vidéo). Le visionnage passé est conservé.`,
      `Reset results for pathway "${p.name}"?\n\nThis resets the quiz score to 0 for users who had already COMPLETED all pathway videos (useful after adding a new video). Watch history is preserved.`
    );
    const ok = await confirmDialog(msg);
    if (!ok) return;
    try {
      const r = await axios.post(
        `${API}/api/pathways/${p.id}/reset-user-progress`,
        { only_completed: true, reset_quiz: true, reset_watch: false },
        { withCredentials: true }
      );
      const affected = r?.data?.affected_users ?? 0;
      const total = r?.data?.total_candidates ?? 0;
      toast.success(L(
        lang,
        `Réinitialisé pour ${affected}/${total} utilisateur(s)`,
        `Reset for ${affected}/${total} user(s)`
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    }
  };

  // Vincent B823 (Eric 2026-02-13) — Bouton « Réindexer » par parcours.
  // Cible : rattraper les KUs déjà approuvées mais sans embedding (bug
  // historique des 6 pipelines d'extraction). Backend :
  // POST /api/admin/knowledge/reindex-pathway/{id}
  // (batch 32, ~1-2s / 100 KUs selon charge). N'affecte pas les KUs
  // pending_review — celles-ci sont indexées automatiquement à l'approbation
  // depuis B823 fix.
  const [reindexingId, setReindexingId] = useState(null);
  const reindexPathway = async (p) => {
    const msg = L(
      lang,
      `Réindexer toutes les connaissances du parcours "${p.name}" ?\n\nCela regénère les embeddings des KU liées aux vidéos du parcours (utile pour rattraper le stock historique approuvé sans embedding). Sans impact sur les KU non approuvées.`,
      `Reindex all knowledge of pathway "${p.name}"?\n\nThis regenerates embeddings for KUs of the pathway's videos (useful for legacy approved-without-embedding stock). No impact on unapproved KUs.`
    );
    const ok = await confirmDialog(msg);
    if (!ok) return;
    setReindexingId(p.id);
    try {
      const r = await axios.post(
        `${API}/api/admin/knowledge/reindex-pathway/${p.id}`,
        {},
        { withCredentials: true }
      );
      const proc = r?.data?.processed ?? 0;
      const total = r?.data?.total_kus ?? 0;
      toast.success(L(
        lang,
        `Réindexé : ${proc}/${total} KU du parcours`,
        `Reindexed: ${proc}/${total} KUs of pathway`
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec de la réindexation", "Reindex failed"));
    } finally {
      setReindexingId(null);
    }
  };

  // Vincent B851 (Eric 2026-02-14 SPEC STRICK PROD) — Diagnostic RAG
  // par parcours. Ouvre un modal listant les vidéos du parcours et
  // leur statut (visible/partiel/aveugle au chatbot) + recommandations.
  const [ragDiagLoadingId, setRagDiagLoadingId] = useState(null);
  const [ragDiagData, setRagDiagData] = useState(null);
  const openRagDiagnostic = async (p) => {
    setRagDiagLoadingId(p.id);
    try {
      const r = await axios.get(
        `${API}/api/admin/knowledge/rag-diagnose-pathway`,
        { params: { pathway_id: p.id }, withCredentials: true },
      );
      setRagDiagData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec du diagnostic", "Diagnostic failed"));
    } finally {
      setRagDiagLoadingId(null);
    }
  };

  // Vincent B852 (Eric 2026-02-14 SPEC STRICK PROD) — Fix en 1 clic
  // depuis le modal Diagnostic RAG : approuve toutes les KUs en
  // pending_review sur les vidéos du parcours.
  const [bulkApprovePathwayLoading, setBulkApprovePathwayLoading] = useState(false);
  const bulkApprovePathway = async () => {
    if (!ragDiagData?.pathway?.id) return;
    const pName = ragDiagData.pathway.name;
    const nPending = ragDiagData.totals?.pending_review || 0;
    if (nPending === 0) {
      toast.info(L(lang, "Aucune KU en attente à approuver.", "No pending review KU to approve."));
      return;
    }
    const ok = await confirmDialog(L(
      lang,
      `Approuver les ${nPending} KU(s) en attente sur "${pName}" ?\n\nElles deviendront immédiatement visibles au chatbot (embedding généré automatiquement si absent).`,
      `Approve ${nPending} pending KU(s) on "${pName}"?\n\nThey will become visible to the chatbot immediately (embedding auto-generated if missing).`,
    ));
    if (!ok) return;
    setBulkApprovePathwayLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/knowledge/bulk-approve-pathway/${ragDiagData.pathway.id}`,
        {},
        { withCredentials: true },
      );
      toast.success(L(
        lang,
        `${r.data?.count || 0} KU approuvée(s), ${r.data?.embedded || 0} embedding(s) généré(s)`,
        `${r.data?.count || 0} KU approved, ${r.data?.embedded || 0} embedding(s) generated`,
      ), { duration: 8000 });
      // Recharge le diagnostic pour montrer le nouveau state (pending → 0)
      await openRagDiagnostic({ id: ragDiagData.pathway.id });
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec de l'approbation en lot", "Bulk approve failed"));
    } finally {
      setBulkApprovePathwayLoading(false);
    }
  };

  // Vincent B853 (Eric 2026-02-14 SPEC STRICK PROD) — Fix en 1 clic
  // depuis le modal Diagnostic RAG : réindexe les KUs sans embedding
  // (backend endpoint B823 déjà en place — POST /admin/knowledge/reindex-pathway/{id}).
  // Parité UX avec B852 : bouton visible seulement si no_embedding > 0.
  const [reindexPathwayFromModalLoading, setReindexPathwayFromModalLoading] = useState(false);
  const reindexPathwayFromModal = async () => {
    if (!ragDiagData?.pathway?.id) return;
    const pName = ragDiagData.pathway.name;
    const nMissing = ragDiagData.totals?.no_embedding || 0;
    if (nMissing === 0) {
      toast.info(L(lang, "Aucune KU sans embedding à réindexer.", "No KU without embedding to reindex."));
      return;
    }
    const ok = await confirmDialog(L(
      lang,
      `Réindexer les ${nMissing} KU(s) sans embedding sur "${pName}" ?\n\nElles deviendront visibles au chatbot après ré-embedding (peut prendre 30-60s par lot).`,
      `Reindex ${nMissing} KU(s) without embedding on "${pName}"?\n\nThey will become visible to the chatbot after re-embedding (~30-60s per batch).`,
    ));
    if (!ok) return;
    setReindexPathwayFromModalLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/knowledge/reindex-pathway/${ragDiagData.pathway.id}`,
        {},
        { withCredentials: true },
      );
      toast.success(L(
        lang,
        `${r.data?.processed || 0} KU réindexée(s)`,
        `${r.data?.processed || 0} KU reindexed`,
      ), { duration: 8000 });
      await openRagDiagnostic({ id: ragDiagData.pathway.id });
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec de la réindexation", "Reindex failed"));
    } finally {
      setReindexPathwayFromModalLoading(false);
    }
  };


  const togglePublish = async (p) => {
    try {
      await axios.patch(
        `${API}/api/pathways/${p.id}`,
        { is_published: !p.is_published },
        { withCredentials: true }
      );
      toast.success(p.is_published
        ? L(lang, "Parcours dépublié", "Pathway unpublished")
        : L(lang, "Parcours publié", "Pathway published"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid={`pathways-page-${pathwayType}`}>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900 flex items-center gap-2">
            <Path size={28} weight="duotone" /> {pageTitle}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isOnboarding
              ? L(lang, "Parcours obligatoires par niveau d'employé.", "Mandatory pathways by employee level.")
              : L(lang, "Parcours par groupes personnalisés (directeurs, conseillers, etc.).", "Pathways by custom groups (managers, advisors, etc.).")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Vincent #190 (Eric 2026-05-27 round 5) — Tri des parcours par
              date de création, date de révision, ou ordre alphabétique. */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
            data-testid="pathway-sort-select"
            title={L(lang, "Trier les parcours", "Sort pathways")}
          >
            <option value="name_asc">{L(lang, "Alphabétique (A → Z)", "Alphabetical (A → Z)")}</option>
            <option value="name_desc">{L(lang, "Alphabétique (Z → A)", "Alphabetical (Z → A)")}</option>
            <option value="created_desc">{L(lang, "Date création (plus récent)", "Created date (newest)")}</option>
            <option value="created_asc">{L(lang, "Date création (plus ancien)", "Created date (oldest)")}</option>
            <option value="updated_desc">{L(lang, "Date révision (plus récent)", "Revision date (newest)")}</option>
            <option value="updated_asc">{L(lang, "Date révision (plus ancien)", "Revision date (oldest)")}</option>
            {/* Vincent XLS iter103 (item 20) — Tri par % d'achèvement */}
            <option value="completion_asc">{L(lang, "% achèvement (croissant)", "Completion % (ascending)")}</option>
            <option value="completion_desc">{L(lang, "% achèvement (décroissant)", "Completion % (descending)")}</option>
          </select>
          <button
            onClick={startNew}
            className="px-4 py-2 bg-[#002FA7] text-white rounded font-semibold inline-flex items-center gap-2 hover:bg-[#001a5c]"
            data-testid="btn-new-pathway"
          >
            <Plus size={16} weight="bold" /> {L(lang, "Nouveau parcours", "New pathway")}
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-center py-12 text-zinc-400">{L(lang, "Chargement…", "Loading…")}</div>
      ) : pathways.length === 0 ? (
        <div className="text-center py-16 bg-amber-50 rounded-xl border-2 border-dashed border-amber-300" data-testid="pathway-empty-warning">
          <p className="text-amber-700 font-semibold mb-2">
            ⚠️ {L(lang, "Veuillez sélectionner, avec l'explorateur de fichiers, au moins 1 fichier à intégrer dans ce parcours",
                       "Please select, in the file explorer, at least 1 file to include in this pathway")}
          </p>
          <button onClick={startNew} className="text-[#002FA7] hover:underline font-medium mt-3">
            {L(lang, "Ou créer un parcours manuellement →", "Or create a pathway manually →")}
          </button>
        </div>
      ) : pathways.every(p => (p.video_ids || []).length === 0) ? (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-800" data-testid="pathway-empty-warning">
          ⚠️ {L(lang, "Aucun parcours ne contient encore de vidéo. Allez dans l'Explorateur de fichiers pour assigner des vidéos à un parcours.",
                       "No pathway has videos yet. Go to File Explorer to assign videos to a pathway.")}
        </div>
      ) : null}
      {pathways.length > 0 && (
        <ul className="space-y-3" data-testid="pathways-list">
          {/* Vincent #190 — Tri appliqué côté client (le backend renvoie par
              created_at desc par défaut). */}
          {[...pathways].sort((a, b) => {
            const ta = (k) => new Date(a[k] || a.created_at || 0).getTime();
            const tb = (k) => new Date(b[k] || b.created_at || 0).getTime();
            // Vincent XLS iter103 — % d'achèvement = ratio des vidéos vues
            // (utilise completion_pct si présent sinon 0).
            const pct = (p) => Number(p.completion_pct ?? p.progress_pct ?? 0);
            switch (sortBy) {
              case "created_asc": return ta("created_at") - tb("created_at");
              case "created_desc": return tb("created_at") - ta("created_at");
              case "updated_desc": return tb("updated_at") - ta("updated_at");
              case "updated_asc": return ta("updated_at") - tb("updated_at");
              case "name_desc": return (b.name || "").localeCompare(a.name || "", "fr", { sensitivity: "base", numeric: true });
              case "completion_asc": return pct(a) - pct(b);
              case "completion_desc": return pct(b) - pct(a);
              case "name_asc":
              default:
                return (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base", numeric: true });
            }
          }).map((p) => {
            const isExpanded = expandedIds.has(p.id);
            // Vincent B1102 (Eric 2026-09-07) — Décompte RÉEL : `video_ids`
            // peut contenir des références orphelines (fichiers supprimés).
            // Le backend renvoie dans `videos` uniquement les fichiers qui
            // existent réellement → c'est la source de vérité affichée. Les
            // orphelins sont signalés explicitement au lieu de gonfler le
            // compte en silence.
            const cardIds = p.video_ids || [];
            const resolvedVideos = Array.isArray(p.videos)
              ? cardIds.map((vid) => p.videos.find((v) => v.id === vid) || allVideos.find((v) => v.id === vid)).filter(Boolean)
              : cardIds.map((vid) => allVideos.find((v) => v.id === vid)).filter(Boolean);
            const orphanCount = Array.isArray(p.videos) ? Math.max(0, cardIds.length - resolvedVideos.length) : 0;
            return (
            <li
              key={p.id}
              className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-[#002FA7]/40 hover:shadow-sm transition-all cursor-pointer"
              data-testid={`pathway-card-${p.id}`}
              onClick={(e) => {
                // Vincent #262 — Ouvre l'aperçu seulement si on n'a pas cliqué sur un
                // bouton d'action (Éditer/Publier/Supprimer) ni un Link interne.
                if (e.target.closest("button, a, input, select, textarea")) return;
                setPreviewing(p);
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {/* Vincent B623 (Eric 2026-07-30 SPEC STRICK PROD) —
                        Triangle collapse/expand à GAUCHE du titre. Par défaut
                        seul le dernier consulté est ouvert (persisté via
                        user.preferences.pathway_last_opened_admin_{type}). */}
                    <button
                      type="button"
                      onClick={(e) => toggleExpand(p.id, e)}
                      className="p-0.5 -ml-1 rounded hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800 flex-shrink-0 transition-colors"
                      data-testid={`pathway-toggle-${p.id}`}
                      title={isExpanded ? L(lang, "Fermer ce parcours", "Collapse this pathway") : L(lang, "Ouvrir ce parcours", "Expand this pathway")}
                      aria-expanded={isExpanded}
                      aria-controls={`pathway-card-body-${p.id}`}
                    >
                      {isExpanded ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
                    </button>
                    <h3 className="font-semibold text-zinc-900 text-base truncate">{p.name}</h3>
                    {/* Vincent B636 (Eric 2026-08-01 SPEC STRICK PROD) —
                        L'ancien badge d'état passif (span non-cliquable) juste à
                        droite du titre a été RETIRÉ. Le bouton cliquable Publié/
                        Brouillon (colonne actions à droite) est désormais le
                        SEUL indicateur d'état — visuel unifié entre les 2 listes
                        (Parcours d'intégration + Parcours d'apprentissage), UX
                        cohérente, plus de doublon. Voir bloc actions ligne ~443
                        pour le bouton conservé. */}
                  </div>
                  {p.description && <p className="text-xs text-zinc-600 mb-2">{p.description}</p>}
                  {isExpanded && (
                  <div data-testid={`pathway-card-body-${p.id}`}>
                  <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                    {/* Vincent B610 (Eric 2026-07-29 SPEC STRICK) — La liste
                        des rôles cibles est affichée UNIQUEMENT en bas de la
                        tuile (via `roleLabel()` avec libellés FR customisés).
                        L'ancien résumé ici affichait les IDs canoniques bruts
                        (« employee_new, manager, … ») → doublon anglais avec
                        les badges du bas → RETIRÉ. On garde seulement le
                        nombre de vidéos. */}
                    <span data-testid={`pathway-video-count-${p.id}`}>
                      {resolvedVideos.length} {L(lang, "vidéos", "videos")}
                    </span>
                    {orphanCount > 0 && (
                      <span
                        className="px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800"
                        title={L(lang,
                          "Ce parcours référence des fichiers qui n'existent plus (supprimés). Modifiez le parcours et enregistrez pour nettoyer.",
                          "This pathway references files that no longer exist (deleted). Edit the pathway and save to clean up.")}
                        data-testid={`pathway-orphan-badge-${p.id}`}
                      >
                        {L(lang, `⚠ ${orphanCount} référence(s) orpheline(s)`, `⚠ ${orphanCount} orphan reference(s)`)}
                      </span>
                    )}
                  </div>
                  {/* Vincent #203 (Eric 2026-05-28) — Dates de création / dernière
                      modification visibles sur la card du parcours. */}
                  {(p.created_at || p.updated_at) && (
                    <div className="mt-1 flex items-center gap-3 text-[10px] text-zinc-400" data-testid={`pathway-dates-${p.id}`}>
                      {p.created_at && (
                        <span title={new Date(p.created_at).toLocaleString("fr-CA")}>
                          {L(lang, "Créé le", "Created")} {new Date(p.created_at).toLocaleDateString("fr-CA")}
                        </span>
                      )}
                      {p.updated_at && p.updated_at !== p.created_at && (
                        <span title={new Date(p.updated_at).toLocaleString("fr-CA")}>
                          · {L(lang, "Modifié le", "Updated")} {new Date(p.updated_at).toLocaleDateString("fr-CA")}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Vincent #164/#165 (Eric 2026-05-26 round 4) — Affichage
                      de l'aperçu des vidéos sélectionnées dans la card. Avant,
                      la card disait "4 vidéos" mais aucun titre n'apparaissait
                      → l'utilisateur ne pouvait pas savoir ce qu'il avait
                      ajouté sans cliquer Éditer. */}
                  {resolvedVideos.length > 0 && (
                    <ol className="mt-2 space-y-0.5 pl-3 text-[11px] text-zinc-700" data-testid={`pathway-card-videos-${p.id}`}>
                      {resolvedVideos.slice(0, 5).map((v, idx) => {
                        const vid = v.id;
                        // Vincent B609 (Eric 2026-07-30 SPEC STRICK) — RACINE DU
                        // BUG « (Sans titre) » : on cherchait dans `allVideos`
                        // (issu de /api/videos qui EXCLUT les documents texte
                        // `is_text_doc=true`). Résultat : `v` était undefined
                        // pour tout document texte du parcours → fallback direct
                        // sur "(Sans titre)". Le backend enrichit désormais
                        // `p.videos` avec title résolu (voir routers/pathways.py :
                        // `_attach_video_summaries`). On utilise cette liste en
                        // priorité, puis on retombe sur `allVideos` (rétrocompat).
                        // Vincent B1102 — La résolution est faite en amont
                        // (`resolvedVideos`), qui exclut aussi les orphelins.
                        // Vincent #374 (Eric 2026-06-23 SPEC STRICK) — JAMAIS de
                        // code "Vidéo XXX..." dans les parcours. Fallback intelligent :
                        // 1. title du document, 2. original_filename, 3. "(Sans titre)".
                        const cleanFilename = (fn) => (fn || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim();
                        const title = v?.title || cleanFilename(v?.original_filename) || L(lang, "(Sans titre)", "(Untitled)");
                        return (
                          <li key={vid} className="truncate">
                            <span className="text-zinc-400 mr-1">{idx + 1}.</span>
                            {/* Bogue 1240 — État de l'élément DANS ce parcours,
                                visible sans ouvrir l'éditeur. */}
                            {p.is_published && (p.item_status || {})[vid] === "draft" && (
                              <span
                                className="mr-1 px-1 py-0.5 rounded border border-orange-300 bg-orange-50 text-[9px] font-bold uppercase text-orange-700"
                                data-testid={`pathway-card-item-draft-${vid}`}
                                title={L(lang, "Brouillon dans ce parcours : invisible pour l'apprenant", "Draft in this pathway: hidden from learners")}
                              >
                                {L(lang, "Brouillon", "Draft")}
                              </span>
                            )}
                            {/* Vincent #262 (2026-02-06) — Titre cliquable
                                pointant vers la fiche vidéo. Permet de
                                consulter rapidement le contenu du parcours
                                sans passer par l'éditeur. */}
                            <Link
                              to={`/dashboard/videos/${vid}`}
                              className="text-[#002FA7] hover:underline"
                              data-testid={`pathway-video-link-${p.id}-${vid}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {title}
                            </Link>
                          </li>
                        );
                      })}
                      {resolvedVideos.length > 5 && (
                        <li className="text-zinc-400 italic">
                          {L(lang, `… et ${resolvedVideos.length - 5} autre(s)`, `… and ${resolvedVideos.length - 5} more`)}
                        </li>
                      )}
                    </ol>
                  )}
                  </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Vincent #269 (2026-06-11) — Bouton texte "Publié"/"Brouillon"
                      (remplace l'icône œil) : indique l'état actuel et bascule au clic.
                      Vincent B636 (Eric 2026-08-01 SPEC STRICK PROD) — Format
                      unifié avec l'ancien badge d'état gauche (retiré) : chip
                      compact `text-[10px] px-2 py-0.5 rounded border`, orange
                      vif pour Brouillon, vert franc pour Publié. Un SEUL indicateur
                      d'état par parcours désormais, clairement cliquable. */}
                  <button
                    onClick={() => togglePublish(p)}
                    className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border shadow-sm transition-all hover:shadow ${
                      p.is_published
                        ? "bg-green-600 text-white border-green-700 hover:bg-green-700"
                        : "bg-orange-500 text-white border-orange-600 hover:bg-orange-600"
                    }`}
                    title={p.is_published
                      ? L(lang, "Cliquer pour repasser en Brouillon", "Click to switch back to Draft")
                      : L(lang, "Cliquer pour Publier", "Click to Publish")}
                    data-testid={`pathway-publish-${p.id}`}
                  >
                    {p.is_published ? L(lang, "Publié", "Published") : L(lang, "Brouillon", "Draft")}
                  </button>
                  {/* Vincent #297 (XLS+PDF 2026-06-16) — REJET de la modale.
                      Le bouton "Visualiser" doit basculer la vue utilisateur
                      vers `employee_trained` (alias backend "user:intermediaire")
                      puis navigate() vers la VRAIE page apprenant des parcours.
                      Pas de mini-fenêtre. */}
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/api/auth/view-as`,
                          { role: "user:intermediaire" },
                          { withCredentials: true }
                        );
                        await refreshUser();
                        try {
                          window.dispatchEvent(new CustomEvent("knoria:user-changed", {
                            detail: { reason: "visualize-pathway", role: "user:intermediaire" }
                          }));
                        } catch (_) { /* old browser */ }
                        toast.success(lang === "fr"
                          ? "👁️ Mode 'Vue apprenant' activé — utilise le sélecteur de rôle (haut-droite) pour revenir."
                          : "👁️ 'Learner view' enabled — use the role switcher (top-right) to return.");
                        const dest = pathwayType === "onboarding"
                          ? "/dashboard/onboarding"
                          : "/dashboard/learning-path";
                        navigate(dest);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || (lang === "fr"
                          ? "Échec de la bascule en mode apprenant"
                          : "Failed to switch to learner view"));
                      }
                    }}
                    className="px-2 py-1 text-xs bg-[#002FA7]/10 text-[#002FA7] rounded hover:bg-[#002FA7]/20 font-medium inline-flex items-center gap-1"
                    data-testid={`pathway-view-${p.id}`}
                    title={L(lang, "Visualiser comme un apprenant (bascule en vue 'employee_trained' et ouvre la page des parcours)", "View as a learner (switches to employee_trained view and opens the pathways page)")}
                  >
                    <Eye size={13} /> {L(lang, "Visualiser", "View")}
                  </button>
                  <button
                    onClick={() => setEditing(p)}
                    className="px-2 py-1 text-xs bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200"
                    data-testid={`pathway-edit-${p.id}`}
                  >
                    {L(lang, "Éditer", "Edit")}
                  </button>
                  <button
                    onClick={() => resetPathwayResults(p)}
                    className="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 inline-flex items-center gap-1"
                    data-testid={`pathway-reset-results-${p.id}`}
                    title={L(lang, "Réinitialiser les résultats des utilisateurs (quiz à 0%)", "Reset user results (quiz to 0%)")}
                  >
                    <ArrowsClockwise size={12} />
                    {L(lang, "Réinitialiser les résultats", "Reset results")}
                  </button>
                  {/* Vincent B823 (Eric 2026-02-13) — Bouton réindex par parcours.
                      Vincent B841 (Eric 2026-02-14 SPEC STRICK PROD) — Grisé et
                      non-cliquable quand le parcours est en brouillon (aucune
                      raison de réindexer des KUs qui ne sont pas encore
                      publiées). */}
                  <button
                    onClick={() => p.is_published && reindexPathway(p)}
                    disabled={reindexingId === p.id || !p.is_published}
                    className={`px-2 py-1 text-xs rounded inline-flex items-center gap-1 disabled:cursor-not-allowed ${
                      p.is_published
                        ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                        : "bg-zinc-100 text-zinc-400 opacity-60"
                    }`}
                    data-testid={`pathway-reindex-${p.id}`}
                    title={
                      !p.is_published
                        ? L(lang, "Publiez d'abord le parcours pour pouvoir le réindexer.", "Publish the pathway first to reindex it.")
                        : L(lang, "Réindexer les KU des vidéos de ce parcours (rattrape le stock historique sans embedding)", "Reindex KUs of this pathway's videos (backfill legacy stock without embedding)")
                    }
                  >
                    <Cube size={12} weight={reindexingId === p.id ? "regular" : "duotone"} className={reindexingId === p.id ? "animate-spin" : ""} />
                    {reindexingId === p.id
                      ? L(lang, "Réindexation…", "Reindexing…")
                      : L(lang, "Réindexer", "Reindex")}
                  </button>
                  {/* Vincent B851 (Eric 2026-02-14 SPEC STRICK PROD) —
                      Bouton Diagnostic RAG : ouvre un modal listant les
                      vidéos du parcours et leur statut RAG-visible.
                      Permet à Vincent d'identifier depuis Prod pourquoi
                      le chatbot ne trouve pas les vidéos FLHQ. */}
                  <button
                    onClick={() => openRagDiagnostic(p)}
                    disabled={ragDiagLoadingId === p.id}
                    className="px-2 py-1 text-xs bg-amber-50 text-amber-800 rounded hover:bg-amber-100 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`pathway-rag-diag-${p.id}`}
                    title={L(
                      lang,
                      "Vérifie combien de KU de ce parcours sont visibles au chatbot",
                      "Check how many KUs of this pathway are visible to the chatbot",
                    )}
                  >
                    <MagnifyingGlass size={12} weight="duotone" />
                    {ragDiagLoadingId === p.id
                      ? L(lang, "Analyse…", "Analyzing…")
                      : L(lang, "Diagnostic RAG", "RAG Diagnostic")}
                  </button>
                  <button
                    onClick={() => removePathway(p)}
                    className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                    data-testid={`pathway-delete-${p.id}`}
                    title={L(lang, "Supprimer", "Delete")}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {/* Vincent #269 (2026-06-11) — Liste des rôles (clés techniques)
                  qui peuvent visualiser ce parcours, en bas de la tuile.
                  Vincent B623 (Eric 2026-07-30 PROD) — masqué quand le
                  parcours est replié. */}
              {isExpanded && (
              <div className="mt-3 pt-2 border-t border-zinc-100 flex items-center gap-1.5 flex-wrap" data-testid={`pathway-roles-${p.id}`}>
                <Users size={12} className="text-zinc-400 flex-shrink-0" />
                {(p.target_groups || []).length > 0 ? (
                  (() => {
                    const ord = (id) => {
                      const i = CANONICAL_ROLE_IDS.indexOf(id);
                      return i === -1 ? 999 : i;
                    };
                    return [...p.target_groups]
                      .sort((a, b) => ord(a) - ord(b))
                      .map((g) => (
                        <span key={g} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#002FA7]/5 text-[#002FA7] border border-[#002FA7]/15">
                          {roleLabel(g)}
                        </span>
                      ));
                  })()
                ) : (
                  <span className="text-[10px] text-zinc-400 italic">
                    {L(lang, "Aucun rôle ciblé — ouvert aux rôles par défaut du type de parcours", "No targeted role — open to default roles for this pathway type")}
                  </span>
                )}
              </div>
              )}
            </li>
          );})}
        </ul>
      )}

      {editing && (
        <PathwayEditor
          pathway={editing}
          allVideos={allVideos}
          isOnboarding={isOnboarding}
          groupSuggestions={groupSuggestions}
          lang={lang}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* Vincent #297 (Eric 2026-06-17 SPEC STRICK) — Suppression DÉFINITIVE de
          la mini-fenêtre d'aperçu. "Visualiser" bascule maintenant sur la VRAIE
          page apprenant (déjà géré par le bouton ci-dessus). Le state previewing
          est conservé pour compat mais ne s'ouvre plus depuis le clic-card. */}
      {previewing && (
        <PathwayPreviewModal
          pathway={previewing}
          allVideos={allVideos}
          lang={lang}
          onClose={() => setPreviewing(null)}
          onEdit={() => { setEditing(previewing); setPreviewing(null); }}
        />
      )}

      {/* Vincent #269 (2026-06-11) — Bouton "Visualiser" : vue apprenant
          en Mode de démonstration (exactement ce que voit un non-éditeur).
          Vincent B609 (Eric 2026-07-30 SPEC STRICK) — On préfère `p.videos`
          (enrichi par le backend, inclut les text docs avec titre résolu)
          plutôt qu'un lookup dans `allVideos` qui exclut `is_text_doc=true`. */}
      {viewingAsLearner && (
        <PathwayLearnerModal
          pathway={viewingAsLearner}
          videos={(viewingAsLearner.video_ids || []).map((vid) => (viewingAsLearner.videos || []).find((v) => v.id === vid) || allVideos.find((v) => v.id === vid)).filter(Boolean)}
          demoMode={true}
          lang={lang}
          onClose={() => setViewingAsLearner(null)}
        />
      )}

      {/* Vincent B851 (Eric 2026-02-14 SPEC STRICK PROD) — Modal
          Diagnostic RAG par parcours. Rend visible en 1 clic pourquoi
          telle vidéo n'apparait pas dans les sources du chatbot. */}
      {ragDiagData && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setRagDiagData(null)}
          data-testid="pathway-rag-diag-modal"
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {L(lang, "Diagnostic RAG", "RAG Diagnostic")}
                </div>
                <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                  {ragDiagData.pathway.name}
                </h2>
                <div className="text-[11px] text-zinc-500">
                  {ragDiagData.pathway.video_count} {L(lang, "vidéo(s)", "video(s)")} ·{" "}
                  <span className={ragDiagData.pathway.is_published ? "text-emerald-700" : "text-amber-700"}>
                    {ragDiagData.pathway.is_published ? L(lang, "Publié", "Published") : L(lang, "Brouillon", "Draft")}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setRagDiagData(null)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded"
                data-testid="pathway-rag-diag-close"
              >
                <X size={18} />
              </button>
            </header>
            <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 grid grid-cols-4 gap-3 text-center">
              <div>
                <div className="text-[10px] uppercase text-zinc-500">{L(lang, "KUs total", "Total KUs")}</div>
                <div className="text-lg font-bold tabular-nums">{ragDiagData.totals.total_kus}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">{L(lang, "Visible chatbot", "RAG-visible")}</div>
                <div className="text-lg font-bold tabular-nums text-emerald-700">{ragDiagData.totals.visible_to_rag}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">{L(lang, "En attente", "Pending review")}</div>
                <div className="text-lg font-bold tabular-nums text-amber-700">{ragDiagData.totals.pending_review}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">{L(lang, "Sans embedding", "No embedding")}</div>
                <div className="text-lg font-bold tabular-nums text-rose-700">{ragDiagData.totals.no_embedding}</div>
              </div>
            </div>
            {ragDiagData.recommendations?.length > 0 && (
              <div className="px-5 py-3 border-b border-zinc-200 bg-amber-50/50 dark:bg-amber-900/10">
                <div className="text-[10px] uppercase text-amber-900 dark:text-amber-200 mb-1 font-semibold">
                  {L(lang, "Recommandations", "Recommendations")}
                </div>
                <ul className="text-xs space-y-1 text-zinc-800 dark:text-zinc-200">
                  {ragDiagData.recommendations.map((r, i) => (
                    <li key={i} data-testid={`pathway-rag-diag-reco-${i}`}>{r}</li>
                  ))}
                </ul>
                {/* Vincent B852 (Eric 2026-02-14 SPEC STRICK PROD) —
                    Bouton fix en 1 clic quand pending_review > 0.
                    Vincent B853 (Eric 2026-02-14) — Ajout du 2e bouton fix
                    en 1 clic pour no_embedding > 0. */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {(ragDiagData.totals?.pending_review || 0) > 0 && (
                    <button
                      onClick={bulkApprovePathway}
                      disabled={bulkApprovePathwayLoading}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm disabled:opacity-50"
                      data-testid="pathway-rag-diag-bulk-approve-btn"
                    >
                      {bulkApprovePathwayLoading
                        ? L(lang, "Approbation…", "Approving…")
                        : L(
                            lang,
                            `✓ Approuver les ${ragDiagData.totals.pending_review} KU en attente`,
                            `✓ Approve ${ragDiagData.totals.pending_review} pending KU(s)`,
                          )}
                    </button>
                  )}
                  {(ragDiagData.totals?.no_embedding || 0) > 0 && (
                    <button
                      onClick={reindexPathwayFromModal}
                      disabled={reindexPathwayFromModalLoading}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm disabled:opacity-50"
                      data-testid="pathway-rag-diag-reindex-btn"
                    >
                      {reindexPathwayFromModalLoading
                        ? L(lang, "Réindexation…", "Reindexing…")
                        : L(
                            lang,
                            `⟳ Réindexer les ${ragDiagData.totals.no_embedding} KU sans embedding`,
                            `⟳ Reindex ${ragDiagData.totals.no_embedding} KU(s) without embedding`,
                          )}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="overflow-y-auto flex-1 p-3">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 dark:bg-zinc-800 text-zinc-600 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">{L(lang, "Vidéo", "Video")}</th>
                    <th className="text-right px-2 py-1.5">KUs</th>
                    <th className="text-right px-2 py-1.5" title={L(lang, "Visibles au chatbot", "Visible to chatbot")}>👁</th>
                    <th className="text-right px-2 py-1.5" title={L(lang, "En attente d'approbation", "Pending review")}>⏳</th>
                    <th className="text-right px-2 py-1.5" title={L(lang, "Sans embedding", "No embedding")}>🔧</th>
                    <th className="text-center px-2 py-1.5">{L(lang, "Statut RAG", "RAG status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ragDiagData.videos.map((v) => (
                    <tr key={v.video_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`pathway-rag-diag-row-${v.video_id}`}>
                      <td className="px-2 py-1.5">
                        <a href={`/dashboard/videos/${v.video_id}`} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">
                          {v.title}
                        </a>
                        {v.pipeline_status && v.pipeline_status !== "complete" && (
                          <span className="ml-2 text-[10px] px-1 rounded bg-amber-100 text-amber-800">{v.pipeline_status}</span>
                        )}
                      </td>
                      <td className="text-right tabular-nums text-zinc-700 px-2 py-1.5">{v.total_kus || 0}</td>
                      <td className="text-right tabular-nums text-emerald-700 px-2 py-1.5">{v.visible_to_rag || 0}</td>
                      <td className="text-right tabular-nums text-amber-700 px-2 py-1.5">{v.pending_review_count || 0}</td>
                      <td className="text-right tabular-nums text-rose-700 px-2 py-1.5">{v.no_embedding_count || 0}</td>
                      <td className="text-center px-2 py-1.5">
                        {v.rag_status === "ok" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">OK</span>}
                        {v.rag_status === "partial" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">PARTIEL</span>}
                        {v.rag_status === "blind" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">AVEUGLE</span>}
                        {v.error === "video_not_found" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">404</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Vincent #262 (Eric 2026-02-06) — Modal d'aperçu : un clic sur la tuile
// d'un parcours ouvre cette fenêtre avec :
//   - la liste COMPLÈTE des vidéos cliquables (pas tronqué à 5)
//   - un CTA "Faire le quiz du parcours" → /dashboard/quiz?theme=<theme>&auto=1
//   - un bouton "Éditer" pour basculer vers l'éditeur si admin
function PathwayPreviewModal({ pathway, allVideos, lang, onClose, onEdit }) {
  // Vincent B609 (Eric 2026-07-30 SPEC STRICK) — Cf. justification dans le
  // rendu principal (PathwaysPage) : `pathway.videos` (enrichi backend) est
  // la source de vérité. `allVideos` exclut les documents texte.
  const videoObjs = (pathway.video_ids || [])
    .map(vid => (pathway.videos || []).find(v => v.id === vid) || allVideos.find(v => v.id === vid))
    .filter(Boolean);
  // Thème dominant du parcours pour pré-filtrer le quiz
  const themeCounts = {};
  videoObjs.forEach(v => {
    const t = v.training_theme;
    if (t) themeCounts[t] = (themeCounts[t] || 0) + 1;
  });
  const dominantTheme = Object.entries(themeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "all";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pathway-preview-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`pathway-preview-modal-${pathway.id}`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-200 bg-gradient-to-r from-[#002FA7]/5 to-transparent flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Path size={20} weight="duotone" className="text-[#002FA7] flex-shrink-0" />
              <h2 className="font-bold text-lg text-zinc-900 truncate">{pathway.name}</h2>
              {pathway.is_published ? (
                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-teal-100 text-teal-700 flex-shrink-0">
                  {L(lang, "Publié", "Published")}
                </span>
              ) : (
                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-zinc-100 text-zinc-600 flex-shrink-0">
                  {L(lang, "Brouillon", "Draft")}
                </span>
              )}
            </div>
            {pathway.description && <p className="text-sm text-zinc-600">{pathway.description}</p>}
            <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500">
              <span>{videoObjs.length} {L(lang, "vidéo(s) à visionner", "video(s) to watch")}</span>
              {dominantTheme !== "all" && (
                <span>· {L(lang, "Thème principal", "Main theme")}: <strong className="text-zinc-700">{dominantTheme}</strong></span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-100 rounded flex-shrink-0"
            data-testid="pathway-preview-close"
            title={L(lang, "Fermer", "Close")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Video list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
            {L(lang, "Formations à visionner dans l'ordre", "Trainings to watch in order")}
          </h3>
          {videoObjs.length === 0 ? (
            <p className="text-sm text-zinc-400 italic py-4 text-center">
              {L(lang, "Aucune vidéo dans ce parcours.", "No videos in this pathway.")}
            </p>
          ) : (
            <ol className="space-y-2" data-testid="pathway-preview-video-list">
              {videoObjs.map((v, idx) => (
                <li key={v.id} className="flex items-stretch gap-2 min-w-0">
                  <Link
                    to={`/dashboard/videos/${v.id}`}
                    /* Bogue 1190 (Éric 2026-06-25) — `min-w-0` sur le lien :
                       sans lui, le titre imposait sa largeur et poussait le
                       bouton « Quiz » hors de la fenêtre. `min-h` donne à
                       toutes les cases la même hauteur, qu'il y ait une ou
                       trois lignes de titre. */
                    className="flex-1 min-w-0 flex items-center gap-3 p-3 min-h-[4.25rem] rounded-lg border border-zinc-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-all group"
                    data-testid={`pathway-preview-video-${v.id}`}
                  >
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-zinc-100 group-hover:bg-[#002FA7] text-zinc-600 group-hover:text-white text-xs font-bold flex-shrink-0 transition-colors">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 break-words whitespace-normal">{v.title}</p>
                      {v.training_theme && (
                        <p className="text-[11px] text-zinc-500">{v.training_theme}</p>
                      )}
                    </div>
                    <span className="text-[11px] text-[#002FA7] font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 whitespace-nowrap">
                      {L(lang, "Voir →", "Watch →")}
                    </span>
                  </Link>
                  {/* Vincent #262/#269 — Quiz par vidéo.
                      Vincent B615 (Eric 2026-07-30 SPEC STRICK) — Retire le
                      logo 🎯 devant « Quiz » (parité stricte avec Vue
                      apprenant LearnerPathwaysView.js:378 et VideoDetailHeader).
                      Ce composant étant un preview admin, l'état
                      « quiz réussi » n'est pas disponible → pas de check vert. */}
                  <Link
                    to={`/dashboard/quiz?video_id=${v.id}&auto=1`}
                    onClick={onClose}
                    className="text-[11px] px-2 py-1 rounded border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 font-semibold whitespace-nowrap flex-shrink-0 self-center"
                    data-testid={`pathway-preview-video-quiz-${v.id}`}
                  >
                    {L(lang, "Quiz", "Quiz")}
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3">
          <button
            onClick={onEdit}
            className="text-xs px-3 py-2 rounded border border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-700 font-medium"
            data-testid="pathway-preview-edit-btn"
          >
            {L(lang, "Éditer le parcours", "Edit pathway")}
          </button>
          {videoObjs.length > 0 ? (
            <PathwayPreviewQuizButton
              pathwayId={pathway.id}
              lang={lang}
              onNavigate={onClose}
            />
          ) : (
            <span className="text-[11px] text-zinc-500 italic" data-testid="pathway-preview-quiz-disabled">
              {L(lang, "🎯 Ajoute des vidéos pour activer le quiz", "🎯 Add videos to enable the quiz")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}



function PathwayPreviewQuizButton({ pathwayId, lang, onNavigate }) {
  // Vincent XLSX 2026-07-13 21h Row 451 — Bouton coloré selon éligibilité,
  // parité avec la vue apprenant.
  const { loading, eligibility } = usePathwayQuizEligibility(pathwayId);
  if (loading || !eligibility) return <span className="text-[11px] text-zinc-400 italic">…</span>;
  const isLocked = eligibility.state === "locked";
  const scoreSuffix = eligibility.pathway_quiz_best_score != null
    ? ` (${Math.round(eligibility.pathway_quiz_best_score)}%)`
    : "";
  if (isLocked) {
    return (
      <button
        type="button"
        onClick={() => toast.info(eligibility.reason_fr, { duration: 6000 })}
        className={pathwayQuizButtonClasses("locked")}
        data-testid="pathway-preview-quiz-btn"
        title={eligibility.reason_fr}
      >
        🔒 {L(lang, "Quiz du parcours verrouillé", "Pathway quiz locked")}
      </button>
    );
  }
  return (
    <Link
      to={`/dashboard/quiz?pathway_id=${encodeURIComponent(pathwayId)}&num_questions=7&auto=1`}
      onClick={onNavigate}
      className={pathwayQuizButtonClasses(eligibility.state)}
      data-testid="pathway-preview-quiz-btn"
      title={eligibility.reason_fr}
    >
      {eligibility.state === "passed"
        ? `✓ ${L(lang, "Quiz du parcours réussi", "Pathway quiz passed")}`
        : `🎯 ${L(lang, "Faire le quiz du parcours", "Take the pathway quiz")}`}
      {scoreSuffix}
    </Link>
  );
}


function PathwayEditor({ pathway, allVideos, isOnboarding, groupSuggestions, lang, onClose, onSaved }) {
  const { user } = useAuth();
  const canonicalRoles = useMemo(() => getCanonicalRoles(user, lang), [user]);
  // Vincent B836 v2 (Eric 2026-02-14 SPEC STRICK PROD) — RETRAIT des rôles
  // « Nouveau » (id: "nouveau") ET « employee_new » (id: "employee_new")
  // des cases cochables de l'éditeur de parcours.
  // Version 1 (B836) ne retirait que "employee_new" et laissait "Nouveau"
  // + rendait "employee_new" via extraSelected. Vincent confirme sur
  // screenshot : LES DEUX doivent disparaître, ainsi que tout token
  // legacy équivalent (nouvel_employe, new_employee) éventuellement
  // stocké dans un parcours ancien.
  const HIDDEN_LEGACY_ROLE_IDS = new Set([
    "nouveau", "new_employee", "nouvel_employe",
    "Nouveau", // sécurité si un ancien parcours stocke le label brut
  ]);
  // Tâche 1189 (Eric 2026-06-25) — « Stagiaire » s'affichait dans la fiche en
  // lecture mais DISPARAISSAIT du formulaire d'édition. Cause : B836 masquait
  // `employee_new` dans l'éditeur parce que l'org le libellait alors
  // « Nouveau » et faisait doublon. Knoria le libelle aujourd'hui
  // « Stagiaire » : c'est un vrai groupe cible, il doit être cochable. On ne
  // masque donc `employee_new` que si son libellé est encore le doublon
  // historique.
  const LEGACY_DUPLICATE_LABELS = new Set([
    "nouveau", "nouvel employé", "nouvel employe", "new", "new employee",
  ]);
  const editableCanonicalRoles = useMemo(
    () => canonicalRoles.filter((r) => {
      if (r.id === "employee_new") {
        return !LEGACY_DUPLICATE_LABELS.has(String(r.label || "").trim().toLowerCase());
      }
      return !HIDDEN_LEGACY_ROLE_IDS.has(r.id);
    }),
    [canonicalRoles],
  );
  const [name, setName] = useState(pathway.name || "");
  const [description, setDescription] = useState(pathway.description || "");
  // Vincent 2026-06-01 — Normaliser les target_groups legacy vers IDs canoniques
  // (nouveau→employee_new, forme→employee_trained, etc.). Pas d'appel backend.
  const [targetGroups, setTargetGroups] = useState(
    (pathway.target_groups || []).map(normalizeGroup)
  );
  const [videoIds, setVideoIds] = useState(pathway.video_ids || []);
  // Bogue 1240 (Eric 2026-06-29) — État Brouillon/Publié PAR ÉLÉMENT du
  // parcours. Un parcours publié pouvait contenir une vidéo pas encore prête :
  // il fallait choisir entre tout publier ou ne rien publier.
  const [itemStatus, setItemStatus] = useState(pathway.item_status || {});
  const [itemBusy, setItemBusy] = useState("");
  // Vincent B595 (Eric 2026-07-28 SPEC STRICK) — Seuils par parcours
  const [videoThresholdPct, setVideoThresholdPct] = useState(
    Number(pathway.video_completion_threshold_pct ?? 75)
  );
  const [quizThresholdPct, setQuizThresholdPct] = useState(
    Number(pathway.quiz_passing_threshold_pct ?? 60)
  );
  // Vincent B595.b (Eric 2026-07-28) — Preview live d'impact des seuils
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Vincent B595.c (Eric 2026-07-28 SPEC STRICK Loi 25) — Audit trail
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [videoSearch, setVideoSearch] = useState("");

  const isNew = !pathway.id;
  // Vincent B1102 (Eric 2026-09-07) — SOURCE DE VÉRITÉ : le tableau `videos`
  // enrichi par le backend (`_attach_video_summaries`) n'a ni filtre `kind`
  // ni limite : il contient TOUS les fichiers du parcours qui existent
  // réellement. `allVideos` (issu de /api/videos, plafonné à 500) sert de
  // complément pour le sélecteur d'ajout. On fusionne les deux pour qu'aucun
  // fichier du parcours ne disparaisse de l'éditeur.
  const videoMap = useMemo(() => {
    const map = new Map(allVideos.map(v => [v.id, v]));
    for (const v of (pathway.videos || [])) {
      if (v && v.id && !map.has(v.id)) map.set(v.id, v);
    }
    return map;
  }, [allVideos, pathway.videos]);
  const selectedVideos = useMemo(
    () => videoIds.map(id => videoMap.get(id)).filter(Boolean),
    [videoIds, videoMap]
  );
  const availableVideos = useMemo(() => {
    // Vincent #179/#180 (Eric 2026-05-26) — Tri alphabétique strict + filtre
    // de réduction (pas champ de recherche libre). Le dropdown affiche TOUTES
    // les vidéos disponibles par ordre alpha, le filtre tape réduit la liste.
    // Tâche 1170 — Accents ignorés pour réduire la liste : « evaluation »
    // doit trouver « Évaluation ». L'affichage garde ses accents.
    const needle = foldAccents(videoSearch);
    const filtered = allVideos.filter(v =>
      !videoIds.includes(v.id) && foldAccents(v.title).includes(needle)
    );
    return filtered.sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base", numeric: true })
    );
  }, [allVideos, videoIds, videoSearch]);

  // Vincent B1102 — Réordonnancement PAR ID (et non par index de la liste
  // affichée) : si une référence orpheline (fichier supprimé) reste dans
  // `video_ids`, les index de la liste affichée et de `videoIds` divergent et
  // les flèches déplaçaient la mauvaise vidéo.
  const move = (videoId, dir) => {
    const arr = [...videoIds];
    const idx = arr.indexOf(videoId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setVideoIds(arr);
  };

  const removeVideo = (id) => setVideoIds(videoIds.filter(x => x !== id));
  const addVideo = (id) => { setVideoIds([...videoIds, id]); };

  // Bogue 1240 — L'état d'un élément est propre à CE parcours : la même vidéo
  // peut être publiée ici et rester en brouillon ailleurs. La bascule part
  // immédiatement au serveur (pas à l'« Enregistrer ») pour que l'apprenant
  // cesse de voir l'élément sans attendre le reste du formulaire.
  const statusOf = (id) => (
    itemStatus[id] || (pathway.is_published ? "published" : "draft")
  );

  const toggleItemStatus = async (id) => {
    if (isNew) {
      toast.info(L(lang,
        "Enregistre le parcours d'abord : l'état de chaque élément suit ensuite.",
        "Save the pathway first: each item's state follows afterwards."));
      return;
    }
    const next = statusOf(id) === "published" ? "draft" : "published";
    setItemBusy(id);
    setItemStatus((prev) => ({ ...prev, [id]: next }));
    try {
      const { data } = await axios.patch(
        `${API}/api/pathways/${pathway.id}/items/${id}/status`,
        { status: next },
        { withCredentials: true },
      );
      setItemStatus(data?.item_status || {});
      toast.success(next === "published"
        ? L(lang, "Élément publié dans ce parcours", "Item published in this pathway")
        : L(lang, "Élément repassé en brouillon", "Item back to draft"));
    } catch (e) {
      setItemStatus((prev) => ({ ...prev, [id]: statusOf(id) }));
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    } finally {
      setItemBusy("");
    }
  };

  const syncItemStatuses = async () => {
    if (isNew) return;
    try {
      const { data } = await axios.post(
        `${API}/api/pathways/${pathway.id}/items/sync-status`,
        {},
        { withCredentials: true },
      );
      setItemStatus(data?.item_status || {});
      toast.success(L(lang,
        `${data?.changed || 0} élément(s) alignés sur l'état du parcours`,
        `${data?.changed || 0} item(s) aligned with the pathway state`));
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    }
  };

  // Vincent B595.b (Eric 2026-07-28 SPEC STRICK) — Debounced live simulation.
  // Chaque changement de seuil déclenche un GET après 500 ms d'inactivité.
  // Skip pour nouveaux parcours (pas encore d'id, pas d'activité à agréger).
  useEffect(() => {
    if (isNew || !pathway.id) return;
    const vt = Math.round(Number(videoThresholdPct));
    const qt = Math.round(Number(quizThresholdPct));
    if (!(vt >= 1 && vt <= 100) || !(qt >= 1 && qt <= 100)) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/api/pathways/${pathway.id}/simulate-thresholds`,
          { params: { video_threshold_pct: vt, quiz_threshold_pct: qt }, withCredentials: true }
        );
        if (!cancelled) setPreview(data);
      } catch (_) { /* silent — non bloquant */ }
      finally { if (!cancelled) setPreviewLoading(false); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [videoThresholdPct, quizThresholdPct, pathway.id, isNew]);

  // Vincent B595.c (Eric 2026-07-28 SPEC STRICK Loi 25) — Chargement audit
  const loadAudit = useCallback(async () => {
    if (isNew || !pathway.id) return;
    try {
      const { data } = await axios.get(
        `${API}/api/pathways/${pathway.id}/threshold-audit?limit=20`,
        { withCredentials: true }
      );
      setAudit(data.entries || []);
    } catch (_) { /* silent */ }
  }, [isNew, pathway.id]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const toggleGroup = (g) => {
    setTargetGroups(targetGroups.includes(g)
      ? targetGroups.filter(x => x !== g)
      : [...targetGroups, g]);
  };

  const save = async () => {
    if (!name.trim()) { toast.error(L(lang, "Nom requis", "Name required")); return; }
    if (targetGroups.length === 0) { toast.error(L(lang, "Au moins un groupe cible requis", "At least one target group required")); return; }
    // Vincent B595 — Validation seuils 1..100
    const vt = Math.round(Number(videoThresholdPct));
    const qt = Math.round(Number(quizThresholdPct));
    if (!(vt >= 1 && vt <= 100) || !(qt >= 1 && qt <= 100)) {
      toast.error(L(lang, "Les seuils doivent être entre 1 % et 100 %.", "Thresholds must be between 1 % and 100 %."));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        target_groups: targetGroups,
        video_ids: videoIds,
        video_completion_threshold_pct: vt,
        quiz_passing_threshold_pct: qt,
      };
      if (isNew) {
        await axios.post(`${API}/api/pathways`, {
          ...payload,
          pathway_type: pathway.pathway_type,
        }, { withCredentials: true });
        toast.success(L(lang, "Parcours créé", "Pathway created"));
      } else {
        await axios.patch(`${API}/api/pathways/${pathway.id}`, payload, { withCredentials: true });
        toast.success(L(lang, "Parcours mis à jour", "Pathway updated"));
        // Vincent B595.c — Recharge audit après sauvegarde (nouvelle entrée si seuil changé)
        loadAudit();
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec de la sauvegarde", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pathway-editor-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white flex items-center justify-between">
          <h2 className="font-bold text-lg">
            {isNew ? L(lang, "Nouveau parcours", "New pathway") : L(lang, "Modifier le parcours", "Edit pathway")}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded" data-testid="pathway-editor-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">{L(lang, "Nom du parcours *", "Pathway name *")}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
              data-testid="pathway-name-input"
              placeholder={L(lang, "Ex : Onboarding semaine 1", "E.g.: Week 1 onboarding")}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">{L(lang, "Description", "Description")}</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
              data-testid="pathway-description-input"
            />
          </div>

          {/* Vincent B595 (Eric 2026-07-28 SPEC STRICK) — Seuils réglables par parcours */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <div className="text-xs font-semibold text-zinc-700 mb-2">
              {L(lang, "Seuils de validation (par parcours)", "Completion thresholds (per pathway)")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-zinc-600 mb-1">
                  {L(lang, "Vidéo vue si visionnée à ≥", "Video seen if watched ≥")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={videoThresholdPct}
                    onChange={(e) => setVideoThresholdPct(e.target.value)}
                    className="w-20 px-2 py-1.5 border border-zinc-300 rounded text-sm"
                    data-testid="pathway-video-threshold-input"
                  />
                  <span className="text-sm text-zinc-600">%</span>
                  <span className="text-[11px] text-zinc-500">{L(lang, "(défaut : 75 %)", "(default: 75 %)")}</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-zinc-600 mb-1">
                  {L(lang, "Quiz validé si score ≥", "Quiz passed if score ≥")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={quizThresholdPct}
                    onChange={(e) => setQuizThresholdPct(e.target.value)}
                    className="w-20 px-2 py-1.5 border border-zinc-300 rounded text-sm"
                    data-testid="pathway-quiz-threshold-input"
                  />
                  <span className="text-sm text-zinc-600">%</span>
                  <span className="text-[11px] text-zinc-500">{L(lang, "(défaut : 60 %)", "(default: 60 %)")}</span>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">
              {L(lang,
                "Ces seuils déterminent quand une vidéo est marquée « vue » et quand un quiz est « validé » pour ce parcours uniquement.",
                "These thresholds decide when a video is « seen » and when a quiz is « passed » for this pathway only."
              )}
            </p>
            {/* Vincent B595.b (Eric 2026-07-28) — Preview live d'impact */}
            {!isNew && preview && preview.total_videos > 0 && (
              <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900" data-testid="pathway-simulation-preview">
                <span className="font-semibold">{L(lang, "Impact simulé", "Simulated impact")} </span>
                {previewLoading && <span className="opacity-60">· {L(lang, "recalcul…", "recomputing…")}</span>}
                {!previewLoading && (
                  <>
                    {L(lang,
                      `À ce jour, ${preview.learners_with_activity} apprenant(s) avec activité : `,
                      `To date, ${preview.learners_with_activity} learner(s) with activity: `
                    )}
                    <strong data-testid="pathway-sim-videos-seen">{preview.videos_seen_count}</strong>{" "}
                    {L(lang, "vidéos vues", "videos seen")}
                    {" · "}
                    <strong data-testid="pathway-sim-quizzes-passed">{preview.quizzes_passed_count}</strong>{" "}
                    {L(lang, "quiz validés", "quizzes passed")}
                    {" · "}
                    <strong data-testid="pathway-sim-total-videos">{preview.total_videos}</strong>{" "}
                    {L(lang, "vidéos au parcours", "videos in pathway")}
                    {preview.learners_fully_completed > 0 && (
                      <>
                        {" · "}
                        <span className="inline-flex items-center gap-1">
                          <strong data-testid="pathway-sim-fully-completed">{preview.learners_fully_completed}</strong>{" "}
                          {L(lang, "apprenant(s) 100 %", "learner(s) 100 %")}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {/* Vincent B595.c (Eric 2026-07-28 SPEC STRICK Loi 25) — Historique d'audit des seuils */}
            {!isNew && audit.length > 0 && (
              <div className="mt-2" data-testid="pathway-threshold-audit">
                <button
                  type="button"
                  onClick={() => setShowAudit(!showAudit)}
                  className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1"
                  data-testid="pathway-threshold-audit-toggle"
                >
                  {showAudit ? "▼" : "▶"} {L(lang, `Historique des seuils (${audit.length})`, `Threshold history (${audit.length})`)}
                </button>
                {showAudit && (
                  <ul className="mt-1 space-y-1 border-l-2 border-zinc-200 pl-3 max-h-40 overflow-y-auto">
                    {audit.map((e) => {
                      const fieldLabel = e.field === "video_completion_threshold_pct"
                        ? L(lang, "Seuil vidéo", "Video threshold")
                        : L(lang, "Seuil quiz", "Quiz threshold");
                      const dt = new Date(e.changed_at);
                      return (
                        <li key={e.id} className="text-[11px] text-zinc-600" data-testid={`pathway-audit-entry-${e.id}`}>
                          <span className="text-zinc-400">{dt.toLocaleString("fr")}</span>{" · "}
                          <strong className="text-zinc-800">{e.changed_by_name}</strong>{" · "}
                          {fieldLabel}{" : "}
                          <span className="text-rose-600">{e.old_value} %</span>{" → "}
                          <span className="text-teal-700 font-semibold">{e.new_value} %</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">
              {L(lang, "Groupes cibles *", "Target groups *")}
            </label>
            {/* Vincent 2026-06-01 — Source unique : les 8 rôles canoniques de "Gestion
                des rôles" (avec libellés personnalisés via roleLabelsFor(user, lang)). Plus de
                liste différente pour onboarding vs apprentissage, plus de champ libre.
                Si le parcours référence un groupe legacy non mappé, on l'affiche tout
                de même en queue de liste pour ne pas perdre la sélection. */}
            {(() => {
              const canonicalIds = editableCanonicalRoles.map(r => r.id);
              // Vincent B836 v2 (Eric 2026-02-14 SPEC STRICK PROD) — On
              // retire aussi les tokens legacy `Nouveau`/`employee_new` des
              // extraSelected (parcours anciens qui les référencent) : ils
              // ne doivent PLUS apparaître dans l'UI, sous aucune forme.
              // Non-cochage automatique : le champ `target_groups` du
              // parcours reste inchangé jusqu'à un nouveau Save (aucune
              // migration silencieuse).
              const extraSelected = targetGroups.filter(g =>
                !canonicalIds.includes(g) && !HIDDEN_LEGACY_ROLE_IDS.has(g)
              );
              const allOptions = [
                ...editableCanonicalRoles,
                ...extraSelected.map(id => ({ id, label: id })),
              ];
              return (
                <div className="flex flex-wrap gap-2" data-testid="pathway-target-groups">
                  {allOptions.map((g) => (
                    <label key={g.id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-zinc-300 cursor-pointer hover:bg-zinc-50">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes(g.id)}
                        onChange={() => toggleGroup(g.id)}
                        data-testid={`pathway-group-${g.id}`}
                      />
                      <span className="text-sm">{g.label}</span>
                    </label>
                  ))}
                </div>
              );
            })()}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-zinc-700">
                {L(lang, "Vidéos du parcours (ordre de visualisation) :", "Pathway videos (watch order):")} {selectedVideos.length}
              </label>
              <div className="flex items-center gap-3">
                {/* Bogue 1240 — Après une série de bascules à la main, un clic
                    remet tout le parcours dans un état homogène. */}
                {!isNew && selectedVideos.length > 0 && (
                  <button
                    type="button"
                    onClick={syncItemStatuses}
                    className="text-xs text-zinc-600 hover:text-zinc-900 hover:underline"
                    data-testid="pathway-items-sync-status"
                    title={L(lang,
                      "Aligner l'état de tous les éléments sur celui du parcours",
                      "Align every item's state with the pathway state")}
                  >
                    {L(lang, "⇄ Synchroniser les états", "⇄ Sync states")}
                  </button>
                )}
                <button
                  onClick={() => setShowAddVideo(!showAddVideo)}
                  className="text-xs text-[#002FA7] hover:underline"
                  data-testid="pathway-toggle-add-video"
                >
                  {showAddVideo ? L(lang, "Fermer", "Close") : L(lang, "+ Ajouter des vidéos", "+ Add videos")}
                </button>
              </div>
            </div>

            {showAddVideo && (
              <div className="border border-zinc-200 rounded p-3 mb-3 bg-zinc-50">
                {/* Vincent #179/#180 (Eric 2026-05-26) — Menu déroulant alphabétique
                    + filtre de réduction (Vincent a explicitement refusé le champ
                    de recherche libre). Le filtre tape réduit la liste affichée
                    dans le <select>. */}
                <div className="mb-2">
                  <label className="block text-[10px] uppercase tracking-wide font-semibold text-zinc-600 mb-1">
                    {L(lang, "Filtrer (réduit la liste) :", "Filter (narrows the list):")}
                  </label>
                  <input
                    type="text"
                    value={videoSearch}
                    onChange={(e) => setVideoSearch(e.target.value)}
                    className="w-full px-2 py-1.5 border border-zinc-300 rounded text-sm"
                    placeholder={L(lang, "Taper un mot pour filtrer…", "Type to filter…")}
                    data-testid="pathway-video-filter"
                  />
                </div>
                <div className="mb-2">
                  <label className="block text-[10px] uppercase tracking-wide font-semibold text-zinc-600 mb-1">
                    {L(lang, "Vidéos disponibles (ordre A → Z) :", "Available videos (A → Z):")}{" "}
                    <span className="text-zinc-400 normal-case">({availableVideos.length})</span>
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        addVideo(e.target.value);
                        e.target.value = "";
                      }
                    }}
                    className="w-full px-2 py-1.5 border border-zinc-300 rounded text-sm bg-white"
                    data-testid="pathway-video-dropdown"
                    defaultValue=""
                  >
                    <option value="" disabled>
                      {availableVideos.length === 0
                        ? L(lang, "— Aucune vidéo disponible —", "— No video available —")
                        : L(lang, "— Sélectionner une vidéo à ajouter —", "— Select a video to add —")}
                    </option>
                    {availableVideos.map((v) => (
                      <option
                        key={v.id}
                        value={v.id}
                        data-testid={`pathway-video-option-${v.id}`}
                      >
                        {v.title || L(lang, "(sans titre)", "(untitled)")}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-[10px] text-zinc-500 italic">
                  {L(
                    lang,
                    "Sélectionner une vidéo l'ajoute à la fin du parcours. Réorganise l'ordre avec les flèches ci-dessous.",
                    "Selecting a video appends it to the pathway. Reorder using arrows below."
                  )}
                </p>
              </div>
            )}

            {selectedVideos.length === 0 ? (
              <p className="text-xs text-zinc-400 italic py-2">{L(lang, "Aucune vidéo sélectionnée.", "No video selected.")}</p>
            ) : (
              <ol className="space-y-1.5">
                {selectedVideos.map((v, idx) => (
                  <li
                    key={v.id}
                    /* Bogue 1190 revu — `min-h` + `items-center` : les cases
                       gardent la même taille quand un titre tient sur deux ou
                       trois lignes, au lieu d'un rectangle plus haut/large que
                       les autres. */
                    className="flex items-center gap-2 px-2 py-1.5 min-h-[3.25rem] w-full min-w-0 bg-zinc-50 border border-zinc-200 rounded text-sm"
                    data-testid={`pathway-video-row-${v.id}`}
                  >
                    <span className="text-[11px] font-mono text-zinc-500 w-6 flex-shrink-0">{idx + 1}.</span>
                    {/* Tâche 1190 (Eric 2026-06-25) — Les titres longs
                        s'enroulent sur plusieurs lignes au lieu de déborder de
                        la fenêtre. `truncate` ne bornait rien : le parent
                        pouvait s'élargir (pas de `min-w-0`). */}
                    <span className="flex-1 min-w-0 break-words whitespace-normal leading-snug">{v.title}</span>
                    {/* Bogue 1240 — Bascule Brouillon/Publié de CET élément
                        dans CE parcours. Même code couleur que le bouton
                        d'état du parcours : vert publié, orange brouillon. */}
                    <button
                      type="button"
                      onClick={() => toggleItemStatus(v.id)}
                      disabled={itemBusy === v.id}
                      className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded border flex-shrink-0 transition-all disabled:opacity-50 ${
                        statusOf(v.id) === "published"
                          ? "bg-green-600 text-white border-green-700 hover:bg-green-700"
                          : "bg-orange-500 text-white border-orange-600 hover:bg-orange-600"
                      }`}
                      data-testid={`pathway-item-status-${v.id}`}
                      data-status={statusOf(v.id)}
                      title={statusOf(v.id) === "published"
                        ? L(lang, "Publié dans ce parcours — cliquer pour repasser en brouillon (l'apprenant ne le verra plus)", "Published in this pathway — click to put back to draft (hidden from learners)")
                        : L(lang, "Brouillon — invisible pour l'apprenant. Cliquer pour publier dans ce parcours", "Draft — hidden from learners. Click to publish in this pathway")}
                    >
                      {statusOf(v.id) === "published"
                        ? L(lang, "Publié", "Published")
                        : L(lang, "Brouillon", "Draft")}
                    </button>
                    <button
                      onClick={() => move(v.id, -1)}
                      disabled={idx === 0}
                      className="p-1 text-zinc-500 hover:bg-zinc-200 rounded disabled:opacity-30"
                      data-testid={`pathway-video-up-${v.id}`}
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => move(v.id, +1)}
                      disabled={idx === selectedVideos.length - 1}
                      className="p-1 text-zinc-500 hover:bg-zinc-200 rounded disabled:opacity-30"
                      data-testid={`pathway-video-down-${v.id}`}
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      onClick={() => removeVideo(v.id)}
                      className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                      data-testid={`pathway-video-remove-${v.id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <footer className="px-6 py-3 border-t border-zinc-200 sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200" data-testid="pathway-cancel">
            {L(lang, "Annuler", "Cancel")}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#002FA7] text-white rounded font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="pathway-save"
          >
            <FloppyDisk size={14} /> {saving ? L(lang, "Enregistrement…", "Saving…") : L(lang, "Enregistrer", "Save")}
          </button>
        </footer>
      </div>
    </div>
  );
}
